// Terminal-event window listeners — extracted from MessageArea.tsx during
// the P5 pass-3 split.
//
// The shell process emits 6 different `terminal:*` window CustomEvents
// (send-to-chat, reply-to-thread, send-to-composer, snapshot,
// share-command, run-result). Each one mutates Zero state (a typed
// message insert / addReply) or feeds the composer.
//
// Every listener is gated on `hidden` so the multiplexed-but-inactive
// MessageArea instances don't respond — without that gate, N mounted
// instances would each fire the same mutator for a single shell event.

import {
  useEffect,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";

type ZeroClient = ReturnType<typeof useZero>;

interface SuggestInsert {
  text: string;
  version: number;
}

interface TerminalEventListenersDeps {
  user: { id: string; display_name?: string | null } | null | undefined;
  channelId: string | null | undefined;
  zero: ZeroClient;
  hidden: boolean;
  setSuggestInsert: Dispatch<SetStateAction<SuggestInsert | null>>;
  suggestInsertVersionRef: RefObject<number>;
}

export function useTerminalEventListeners({
  user,
  channelId,
  zero,
  hidden,
  setSuggestInsert,
  suggestInsertVersionRef,
}: TerminalEventListenersDeps): void {
  // Listen for "Send to Chat" button in terminal — post selected text as a
  // typed terminal_output message (no HTML markers). Gated on `hidden` so
  // only the ACTIVE multiplexed instance responds.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const payload = (e as CustomEvent).detail;
      if (!payload?.content || !user || !channelId) return;
      const now = Date.now();
      zero.mutate(
        mutators.messages.insert({
          id: crypto.randomUUID(),
          channel_id: channelId,
          user_id: user.id,
          content: "",
          kind: "terminal_output",
          payload,
          is_edited: false,
          created_at: now,
          updated_at: now,
        }),
      );
    };
    window.addEventListener("terminal:send-to-chat", handler);
    return () => window.removeEventListener("terminal:send-to-chat", handler);
  }, [channelId, user, zero, hidden]);

  // Listen for "Reply to thread" from terminal — post as threaded reply.
  // Uses addReply mutator to correctly update reply_count/reply_users/latest_reply_at.
  // Gated on `hidden` — only the active multiplex instance responds.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const {
        content,
        channelId: targetChannelId,
        threadId,
      } = (e as CustomEvent).detail;
      if (!content || !user || !targetChannelId || !threadId) return;
      const now = Date.now();
      zero.mutate(
        mutators.messages.addReply({
          parent_id: threadId,
          reply: {
            id: crypto.randomUUID(),
            user_id: user.id,
            content,
            kind: "text",
            is_edited: false,
            created_at: now,
            updated_at: now,
          },
        }),
      );
    };
    window.addEventListener("terminal:reply-to-thread", handler);
    return () =>
      window.removeEventListener("terminal:reply-to-thread", handler);
  }, [user, zero, hidden]);

  // Listen for "Send to Composer" from terminal — insert the selection into the
  // chat composer so the user can edit before sending. Gated on `hidden` so
  // only the active multiplex instance picks it up.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const { text } = (e as CustomEvent).detail ?? {};
      if (typeof text !== "string" || !text.trim()) return;
      const version = ++suggestInsertVersionRef.current;
      setSuggestInsert({ text, version });
      // Clear after a tick so MessageInput processes it once.
      setTimeout(() => setSuggestInsert(null), 100);
    };
    window.addEventListener("terminal:send-to-composer", handler);
    return () =>
      window.removeEventListener("terminal:send-to-composer", handler);
  }, [hidden, setSuggestInsert, suggestInsertVersionRef]);

  // Listen for terminal snapshot events (Cmd+S in shell) — post as card message.
  // Gated on `hidden` — only the active multiplex instance responds.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const { ansi, plain, cwd } = (e as CustomEvent).detail;
      if (!ansi || !plain || !user || !channelId) return;
      const now = Date.now();
      zero.mutate(
        mutators.messages.insert({
          id: crypto.randomUUID(),
          channel_id: channelId,
          user_id: user.id,
          content: "",
          kind: "terminal_snapshot",
          payload: {
            app: "Terminal",
            title: "Terminal Snapshot",
            ansi,
            plain,
            cwd,
            timestamp: now,
          },
          is_edited: false,
          created_at: now,
          updated_at: now,
        }),
      );
    };
    window.addEventListener("terminal:snapshot", handler);
    return () => window.removeEventListener("terminal:snapshot", handler);
  }, [channelId, user, zero, hidden]);

  // Listen for terminal command share events — post as command_history card.
  // Gated on `hidden` — only the active multiplex instance responds.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const entry = (e as CustomEvent).detail;
      if (!entry || !user || !channelId) return;
      const now = Date.now();
      zero.mutate(
        mutators.messages.insert({
          id: crypto.randomUUID(),
          channel_id: channelId,
          user_id: user.id,
          content: "",
          kind: "command_history",
          payload: {
            app: "Terminal",
            title: entry.command,
            ...entry,
          },
          is_edited: false,
          created_at: now,
          updated_at: now,
        }),
      );
    };
    window.addEventListener("terminal:share-command", handler);
    return () => window.removeEventListener("terminal:share-command", handler);
  }, [channelId, user, zero, hidden]);

  // Listen for /run results from terminal — post as a typed `run` card.
  // ShellTerminal now dispatches semantic `{ kind, payload }` in the event
  // detail (not a pre-wrapped marker string).
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const chId = detail?.channelId;
      const userId = detail?.userId;
      if (!chId || !userId) return;
      // Only post if we're still on the same channel
      if (chId !== channelId || userId !== user?.id) return;
      const now = Date.now();
      zero.mutate(
        mutators.messages.insert({
          id: crypto.randomUUID(),
          channel_id: chId,
          user_id: userId,
          content: "",
          kind: detail?.kind ?? "run",
          payload: detail?.payload ?? null,
          is_edited: false,
          created_at: now,
          updated_at: now,
        }),
      );
    };
    window.addEventListener("terminal:run-result", handler);
    return () => window.removeEventListener("terminal:run-result", handler);
  }, [channelId, user, zero, hidden]);
}
